import { z } from 'zod';
import { isoDate, uuid } from './common.js';
import { voucherKind } from './voucher.js';

/** Fixed vouchers: at most what a tier may cost (`tierInput`). */
export const MAX_FIXED_VOUCHER_CENTS = 100_000;

/**
 * Code typed by the organiser: upper-cased like the buyer's input (`voucherCode`), letters,
 * digits, `-` and `_` only so it reads aloud and types on a phone without surprises.
 */
export const adminVoucherCode = z
  .string()
  .trim()
  .min(3)
  .max(32)
  .transform((s) => s.toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9_-]+$/, 'codeChars'));

/** Editable voucher fields (A-02). `uses` is never here: the Stripe webhook owns it. */
const voucherFields = z.object({
  code: adminVoucherCode,
  kind: voucherKind,
  /** Percent: 1–100. Fixed: cents, taken off the eligible formats once per order. */
  value: z.number().int().positive(),
  /** Null = unlimited. */
  maxUses: z.number().int().positive().max(100_000).nullable(),
  /** [validFrom, validUntil); null = open-ended. */
  validFrom: isoDate.nullable(),
  validUntil: isoDate.nullable(),
  /** Formats the discount applies to; empty = every format of the event. */
  formatIds: z.array(uuid).max(50),
});
export type VoucherFields = z.infer<typeof voucherFields>;

type Checked = Partial<Pick<VoucherFields, 'kind' | 'value' | 'validFrom' | 'validUntil'>>;

/**
 * Cross-field rules, shared by the form, the create route and the update route (which runs
 * them on the merged row). Issue messages are i18n keys (`admin.error.<key>`).
 */
export function voucherIssues(v: Checked): { path: keyof VoucherFields; message: string }[] {
  const issues: { path: keyof VoucherFields; message: string }[] = [];
  if (v.kind === 'percent' && v.value !== undefined && (v.value < 1 || v.value > 100))
    issues.push({ path: 'value', message: 'percentRange' });
  if (v.kind === 'fixed' && v.value !== undefined && v.value > MAX_FIXED_VOUCHER_CENTS)
    issues.push({ path: 'value', message: 'fixedRange' });
  if (v.validFrom && v.validUntil && new Date(v.validFrom) >= new Date(v.validUntil))
    issues.push({ path: 'validUntil', message: 'order' });
  return issues;
}

const addIssues = (v: Checked, ctx: z.RefinementCtx) => {
  for (const i of voucherIssues(v))
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i.path], message: i.message });
};

export const createVoucherInput = voucherFields.superRefine(addIssues);
export type CreateVoucherInput = z.infer<typeof createVoucherInput>;

/**
 * A patch: only the fields that change. The route re-runs `voucherIssues` on the merged row,
 * and refuses code, kind, value and formats on a voucher an order uses (frozen).
 */
export const updateVoucherInput = voucherFields.partial().strict().superRefine(addIssues);
export type UpdateVoucherInput = z.infer<typeof updateVoucherInput>;

/** Fields an order's discount was computed from: frozen once any order references the code. */
export const FROZEN_VOUCHER_FIELDS = ['code', 'kind', 'value', 'formatIds'] as const;

export const adminVoucher = z.object({
  id: uuid,
  code: z.string(),
  kind: voucherKind,
  value: z.number().int(),
  maxUses: z.number().int().nullable(),
  validFrom: isoDate.nullable(),
  validUntil: isoDate.nullable(),
  formatIds: z.array(uuid),
  /** Paid orders that used the code (Stripe webhook). */
  uses: z.number().int(),
  /** Pending orders inside their 20-min hold: they count against `maxUses` too. */
  held: z.number().int(),
  /** Orders of any status referencing the code: > 0 = frozen, can't be deleted. */
  orders: z.number().int(),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type AdminVoucher = z.infer<typeof adminVoucher>;

export const adminVouchersOutput = z.object({ vouchers: z.array(adminVoucher) });
export type AdminVouchersOutput = z.infer<typeof adminVouchersOutput>;

/** Where a voucher stands now, for the list badge. */
export type VoucherState = 'active' | 'scheduled' | 'ended' | 'exhausted';
export function voucherState(
  v: Pick<AdminVoucher, 'validFrom' | 'validUntil' | 'maxUses' | 'uses' | 'held'>,
  now: Date,
): VoucherState {
  if (v.validUntil && new Date(v.validUntil) <= now) return 'ended';
  if (v.validFrom && new Date(v.validFrom) > now) return 'scheduled';
  if (v.maxUses !== null && v.uses + v.held >= v.maxUses) return 'exhausted';
  return 'active';
}
